export interface SceneBeat {
  type: 'narration' | 'dialogue' | 'action';
  character?: string;
  expression?: string;
  side?: 'left' | 'right';
  text: string;
}

export interface SceneData {
  id: number;
  title: string;
  location: string;
  beats: SceneBeat[];
}

// Character side assignment
export const CHARACTER_SIDES: Record<string, 'left' | 'right'> = {
  Nina: 'left',
  Anna: 'left',
  Emilie: 'left',
  Raoul: 'right',
  Luca: 'right',
  Youssef: 'right',
};

// Character images
export const CHARACTER_IMAGES: Record<string, any> = {
  Nina: require('../../assets/images/characters/egocrisis_nina.jpg'),
  Raoul: require('../../assets/images/characters/egocrisis_raoul.jpg'),
  Anna: require('../../assets/images/characters/egocrisis_anna.jpg'),
  Emilie: require('../../assets/images/characters/egocrisis_emilie.jpg'),
  Luca: require('../../assets/images/characters/egocrisis_luca.jpg'),
  Youssef: require('../../assets/images/characters/egocrisis_youssef.jpg'),
};

// Character accent colors
export const CHARACTER_COLORS: Record<string, string> = {
  Nina: '#2D6A4F',
  Raoul: '#1E3A5F',
  Anna: '#8B1A4A',
  Emilie: '#C75000',
  Luca: '#6B4C9A',
  Youssef: '#D4A017',
};

const SCENE_DATA: SceneData[] = [
  {
    id: 1,
    title: "Arrivee de Raoul",
    location:
      "Open Space, salle vitree DevSec Lounge, lumiere tamisee, fond sonore de ventilation serveur. L'equipe est reunie autour d'une grande table de reunion. Youssef se leve pour accueillir Raoul a son premier jour.",
    beats: [
      {
        type: 'action',
        text: "Youssef se leve et frappe dans ses mains pour attirer l'attention de toute l'equipe.",
      },
      {
        type: 'dialogue',
        character: 'Youssef',
        expression: 'enthousiaste',
        side: 'right',
        text: "Tout le monde, votre attention s il vous plait ! Je vous presente Raoul, notre nouveau CloudOps.",
      },
      {
        type: 'dialogue',
        character: 'Anna',
        expression: 'souriant',
        side: 'left',
        text: 'Bienvenue Raoul !',
      },
      {
        type: 'narration',
        text: "L'equipe repondait en choeur : Bienvenue Raoul !",
      },
      {
        type: 'dialogue',
        character: 'Youssef',
        expression: 'professionnel',
        side: 'right',
        text: "Laissez-moi vous presenter l'equipe. Voici Anna, notre Product Owner. Elle definit les priorites et s'assure que l'on construit ce que les utilisateurs veulent vraiment.",
      },
      {
        type: 'dialogue',
        character: 'Youssef',
        side: 'right',
        text: "Nina est notre Tech Lead. C'est elle qui pose l'architecture et verifie que tout tient la route techniquement.",
      },
      {
        type: 'dialogue',
        character: 'Youssef',
        side: 'right',
        text: "Luc, notre SysAdmin. Il gere les acces, les machines, et tout ce qui touche aux infras.",
      },
      {
        type: 'dialogue',
        character: 'Youssef',
        side: 'right',
        text: "Et Emilie, notre chercheuse en IA. Elle travaille sur le moteur adaptatif d'EgoCrisis.",
      },
      {
        type: 'narration',
        text: "EgoCrisis — un jeu en realite augmentee dont l'IA adapte les scenarios en temps reel au profil psychologique des joueurs.",
      },
      {
        type: 'dialogue',
        character: 'Youssef',
        expression: 'direct',
        side: 'right',
        text: "Ta premiere mission : creer les identites dans notre tenant Azure — Microsoft Entra ID. On part de zero. Les acces sont a provisionner.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'concentre',
        side: 'right',
        text: 'Montrez-moi la backlog.',
      },
    ],
  },
  {
    id: 2,
    title: 'Identites dans la Matrice',
    location:
      "Open Space, salle vitree DevSec Lounge. Nina tend a Raoul une fiche cartonnee : US-EN-ID-001. Le backlog Agile clignote sur un mur lateral. La tache active : Provisionner les acces de l'equipe Entra ID.",
    beats: [
      {
        type: 'action',
        text: 'Nina tend la fiche US-EN-ID-001 a Raoul.',
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'serieux',
        side: 'left',
        text: "Allez, on commence par le commencement. Deux comptes a creer : le mien et celui de Luc.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'sourcil leve',
        side: 'right',
        text: "Tu vas me dire qu on a besoin d un job title juste pour remplir une case ?",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'sourire en coin',
        side: 'left',
        text: "Pas du tout. Chaque champ est une variable dans une regle d automatisation.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "On a une politique CloudCorp : tout utilisateur avec Cloud Administrator comme titre rejoint automatiquement le groupe des admins cloud — sans intervention humaine.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "Ce champ alimente les regles dynamiques, qui alimentent les groupes, qui declenchent les roles RBAC, qui conditionnent les politiques de securite. C'est une chaine. Tu veux l'automatisation ? Tu definis bien les metadonnees.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'pensif',
        side: 'right',
        text: "Donc si demain Luc devient Product Owner, il saute des groupes ?",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'approuver',
        side: 'left',
        text: "Exactement. Le moteur evalue en temps reel les regles. Le titre devient la source de verite d'acces.",
      },
    ],
  },
  {
    id: 3,
    title: 'Activation des superpouvoirs',
    location:
      "Salle serveur, lumiere bleue. Nina pointe l'ecran et affiche la fiche US-EN-LIC-002. Les racks clignotent. La licence Entra ID P2 est en attente d'activation.",
    beats: [
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'serieux',
        side: 'left',
        text: "Avant d'aller plus loin, on a un prerequis non negligeable.",
      },
      {
        type: 'action',
        text: "Nina affiche la fiche US-EN-LIC-002 sur l'ecran.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "C'est la cle d'activation des super-pouvoirs d'Entra ID. Sans elle, on peut rien automatiser.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'curieux',
        side: 'right',
        text: "Et on peut pas tester en P1 ?",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'ferme',
        side: 'left',
        text: "Bloquant. Les groupes dynamiques, c'est P2 only. Conditional Access aussi. Sans cette licence, on construit sur du sable.",
      },
      {
        type: 'action',
        text: 'Raoul navigue vers le portail Azure, active la licence Entra ID P2 et l\'assigne aux comptes.',
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'satisfait',
        side: 'right',
        text: 'Licence activee et assignee.',
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'sourire',
        side: 'left',
        text: 'Tu vois, avec cette cle, on debloque la suite du lab. Pret pour les groupes ?',
      },
    ],
  },
  {
    id: 4,
    title: 'Groupes dynamiques',
    location:
      "Open Space. Nina dessine un schema sur un tableau blanc : des fleches relient jobTitle aux groupes, aux roles RBAC, aux politiques. Le backlog marque US-EN-GRP-003.",
    beats: [
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'pedagogique',
        side: 'left',
        text: "Ces groupes, ce n'est pas juste pour filtrer. C'est notre moteur d'automatisation de l'acces.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "Un nouveau developpeur IA qui arrive ? On remplit son profil correctement, et bam. Il est dans le bon groupe, avec les bons acces, sans qu'on touche a rien manuellement.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'dubitatif',
        side: 'right',
        text: "Mais ca cree pas des groupes vides et inutiles si personne n'a le role ?",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'decisif',
        side: 'left',
        text: "Si. Et c'est volontaire. Ce sont des squelettes structurels. Ils existent meme sans membre.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "Le titre devient la source de verite d'acces. Pas de ticket. Pas d'admin qui oublie. Le moteur evalue en continu.",
      },
      {
        type: 'action',
        text: "Raoul cree trois groupes : IT Cloud Administrators (dynamique, jobTitle = Cloud Administrator), IT System Administrators (dynamique, jobTitle = System Administrator), IT Lab Administrators (assigne).",
      },
    ],
  },
  {
    id: 5,
    title: 'Le Nexus',
    location:
      "Salle de controle panoramique. La topologie Azure s'affiche sur grand ecran. Youssef pointe le tenant secondaire. Nina et Raoul echangent sur la strategie d'isolation.",
    beats: [
      {
        type: 'dialogue',
        character: 'Youssef',
        expression: 'strategique',
        side: 'right',
        text: "Prochaine etape : le tenant EgoCrisis Labs. On isole la R&D de la production.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'sceptique',
        side: 'right',
        text: "Pourquoi ne pas simplement leur donner un acces invite direct ? C'est plus rapide.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'expliquer',
        side: 'left',
        text: "Quatre raisons. Un : si un compte R&D est compromis, l'attaquant reste dans son bac a sable. Il ne peut pas pivoter vers la prod.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "Deux : les politiques, les licences, les roles — tout est independant. On peut tout effacer la-bas sans toucher a rien ici.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "Trois : les logs sont separes. Si un audit se passe la-bas, il ne melange pas les evenements de prod.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "Quatre : un resource group isole, c'est une frontiere logique. Un tenant separe, c'est une frontiere physique dans l'infrastructure Microsoft.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'convaincu',
        side: 'right',
        text: "C'est vrai que si je dois terraform destroy, je prefere une frontiere dure.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'confirmer',
        side: 'left',
        text: "Le tenant, c'est une frontiere physique, pas une cloison logique. On cree le tenant Cloudchrono Labs maintenant.",
      },
    ],
  },
  {
    id: 6,
    title: 'MFA — Derniere ligne de defense',
    location:
      "Bureau de Nina, 22h. Les ecrans affichent les logs de securite. Nina presente la derniere User Story a Raoul : US-EN-MFA-005. Le tableau blanc montre la politique Conditional Access.",
    beats: [
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'solennel',
        side: 'left',
        text: "Derniere US. Celle-la, elle est non negotiable.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'interrogateur',
        side: 'right',
        text: "Est-ce qu on n'est pas en train de sur-complexifier juste pour faire plaisir aux checklists ISO ?",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'grave',
        side: 'left',
        text: "Non. Laisse-moi te raconter ce qui s'est passe chez un studio partenaire l'annee derniere.",
      },
      {
        type: 'narration',
        text: "Un compte admin sans MFA. Un token vole via une fuite GitHub. Quatre jours d'acces total a la production. Bases de donnees exfiltrees. Le studio a failli fermer.",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        side: 'left',
        text: "Ici, on considere tout compte admin comme compromis par defaut. On construit des murs, pas des portes a cadenas.",
      },
      {
        type: 'dialogue',
        character: 'Raoul',
        expression: 'resolu',
        side: 'right',
        text: "OK. On cible uniquement les membres du groupe IT Cloud Administrators ?",
      },
      {
        type: 'dialogue',
        character: 'Nina',
        expression: 'acquiescer',
        side: 'left',
        text: "Exact. On protege ceux qui ont acces aux roles, aux cles, aux policies. Target resource : Microsoft Admin Portals. Grant : Require MFA. Enable : On.",
      },
      {
        type: 'action',
        text: "Raoul configure la politique Conditional Access et l'active en mode Enforcement.",
      },
      {
        type: 'narration',
        text: "La politique est active. Le tenant est securise. L'equipe EgoCrisis peut travailler en confiance.",
      },
    ],
  },
];

export function getSceneData(sceneId: number): SceneData | undefined {
  return SCENE_DATA.find(s => s.id === sceneId);
}
